import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

let nextTextareaId = 0;

@Component({
  selector: 'fdk-textarea',
  templateUrl: './textarea.component.html',
  styleUrl: './textarea.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FdkTextareaComponent),
      multi: true,
    },
  ],
})
export class FdkTextareaComponent implements ControlValueAccessor {
  @Input({ required: true }) label!: string;
  @Input() placeholder: string = '';
  @Input() value: string = '';
  @Output() readonly valueChange = new EventEmitter<string>();
  @Input() name: string | null = null;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input({ transform: booleanAttribute }) error = false;
  @Input() errorMessage = '';

  readonly textareaId = `fdk-textarea-${nextTextareaId++}`;
  readonly errorMessageId = `${this.textareaId}-error`;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  get showErrorMessage(): boolean {
    return this.error && this.errorMessage.trim().length > 0;
  }

  get describedBy(): string | null {
    return this.showErrorMessage ? this.errorMessageId : null;
  }

  handleInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.value = value;
    this.valueChange.emit(value);
    this.onChange(value);
  }

  handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: string | null | undefined): void {
    this.value = value ?? '';
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetectorRef.markForCheck();
  }
}
